import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { ConfigService } from './config.service';
import { RouteInfo } from '../shared/vertical-menu/vertical-menu.metadata';
import { ROUTES } from '../shared/vertical-menu/vertical-menu-routes.config';
import { Role } from './enum/role';
import { ensureFitterflyAdditivesMenuItem, ensureProductTypeMenuItem, sortMasterDataSubmenu } from '../helper/menu-order.helper';

@Injectable({
  providedIn: 'root'
})
export class EnhancedMenuService {
  private permissionCache = new Map<string, any>();
  readonly menuRefresh$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private configService: ConfigService
  ) {}

  /**
   * Get filtered menu based on user permissions
   */
  async getFilteredMenu(): Promise<RouteInfo[]> {
    const user = this.authService.getLoggedInUser();
    if (!user) {
      return [];
    }

    const userRole = user.currentRoleId || user.roleIds?.[0] || 2;
    const cacheKey = `${user.id}-${userRole}`;

    // Check cache first
    if (this.permissionCache.has(cacheKey)) {
      return this.permissionCache.get(cacheKey);
    }

    try {
      // Get NavTree structure from database
      const navTree = await this.getNavTreeStructure();
      
      if (navTree.length === 0) {
        console.log('No NavTree items found in database');
        return [];
      }
      
      // For SuperAdmin (role 1), check permissions dynamically like other roles
      if (userRole === 1) {
        console.log('SuperAdmin role detected - checking permissions dynamically');
        const permissions = await this.getUserPermissions(user.id, userRole);
        console.log(`SuperAdmin has ${permissions.length} permissions in database`);
        
        if (permissions.length === 0) {
          console.log('SuperAdmin has no permissions - showing empty menu');
          this.permissionCache.set(cacheKey, []);
          return [];
        }
        
      const filteredMenu = this.postProcessMenuForRole(
        this.filterMenuByPermissions(navTree, permissions, userRole),
        userRole
      );
      console.log(`SuperAdmin filtered menu has ${filteredMenu.length} items based on permissions`);
        
        // Cache the result & sync session routes
        this.authService.setVeticalRoutes(filteredMenu);
        this.permissionCache.set(cacheKey, filteredMenu);
        return filteredMenu;
      }
      
      // For other roles, get user permissions from database
      const permissions = await this.getUserPermissions(user.id, userRole);
      console.log(`Found ${permissions.length} permissions for user ${user.id} with role ${userRole}`);
      
      // Filter menu based on database permissions
      const filteredMenu = this.postProcessMenuForRole(
        this.filterMenuByPermissions(navTree, permissions, userRole),
        userRole
      );
      console.log(`Filtered menu has ${filteredMenu.length} items for role ${userRole}`);
      
      // Cache the result & sync session routes
      this.authService.setVeticalRoutes(filteredMenu);
      this.permissionCache.set(cacheKey, filteredMenu);
      
      return filteredMenu;
    } catch (error) {
      console.error('Error getting filtered menu:', error);
      return [];
    }
  }





  /**
   * Get user permissions from database
   */
  private async getUserPermissions(userId: string, userRole: number): Promise<any[]> {
    try {
      const response = await this.configService.getRoleWiseUserPermissionByUserId(userId);
      if (response && response.status === 0) {
        const permissions = Array.isArray(response.response) ? response.response : [];
        return permissions.map(permission => ({
          ...permission,
          navTreeId: String(permission.navTreeId),
          roleId: Number(permission.roleId)
        }));
      }
      return [];
    } catch (error) {
      console.error('Error getting user permissions:', error);
      return [];
    }
  }

  /**
   * Get NavTree structure from database
   */
  private async getNavTreeStructure(): Promise<any[]> {
    try {
      const response = await this.configService.getNavTree();
      if (response && response.status === 0) {
        const raw = Array.isArray(response.response) ? response.response : [];
        const normalized = raw.map(item => ({
          ...item,
          id: String(item.id),
          parentId: item.parentId == null ? null : String(item.parentId),
          level: Number(item.level)
        }));
        return this.deduplicateNavTree(normalized);
      }
      return [];
    } catch (error) {
      console.error('Error getting NavTree structure:', error);
      return [];
    }
  }

  /**
   * Filter menu based on permissions (both role-wise and user-specific)
   */
  private filterMenuByPermissions(navTree: any[], permissions: any[], userRole: number): RouteInfo[] {
    console.log('Filtering menu with permissions:', permissions.length, 'for role:', userRole);
    
    // Map permissions by navTreeId for userRole using the latest permission state
    const permissionMap = new Map<string, boolean>();
    permissions
      .filter(p => String(p.roleId) === String(userRole))
      .forEach(p => {
        const navId = String(p.navTreeId);
        permissionMap.set(navId, !!p.isAccess);
      });

    const accessibleNavTreeIds = new Set<string>();
    permissionMap.forEach((hasAccess, navId) => {
      if (hasAccess) {
        accessibleNavTreeIds.add(navId);
      }
    });
    
    console.log('Accessible NavTree IDs for role', userRole, ':', Array.from(accessibleNavTreeIds));
    
    // Log permission details for debugging
    const rolePermissions = permissions.filter(p => String(p.roleId) === String(userRole));
    console.log(`Found ${rolePermissions.length} permissions for role ${userRole}:`, rolePermissions);
    
    // Build menu from accessible items
    const filteredRoutes: RouteInfo[] = [];
    
    // Get all modules (level 1)
    const modules = navTree.filter(item => item.level === 1).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    
    modules.forEach(module => {
      // Check if module itself is accessible — compare as strings
      const hasModuleAccess = accessibleNavTreeIds.has(String(module.id));
      console.log(`Module ${module.displayName} (${module.id}): hasModuleAccess=${hasModuleAccess}`);
      
      // Get accessible pages for this module — compare parentId and id as strings
      const accessiblePages = navTree.filter(item => 
        item.level === 2 && 
        String(item.parentId) === String(module.id) && 
        accessibleNavTreeIds.has(String(item.id))
      ).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      
      console.log(`  Found ${accessiblePages.length} accessible pages for module ${module.displayName}`);
      
      // Get accessible subpages for each page — compare parentId and id as strings
      const pageRoutes = accessiblePages.map(page => {
        const accessibleSubpages = navTree.filter(item => 
          item.level === 3 && 
          String(item.parentId) === String(page.id) && 
          accessibleNavTreeIds.has(String(item.id))
        ).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        
        console.log(`    Page ${page.displayName}: ${accessibleSubpages.length} accessible subpages`);
        
        const subpageRoutes = accessibleSubpages.map(subpage => ({
          path: subpage.path || '',
          title: subpage.displayName || subpage.moduleName,
          icon: subpage.icon || 'ft-arrow-right submenu-icon',
          class: '',
          badge: subpage.badge || '',
          badgeClass: subpage.badgeClass || '',
          isExternalLink: subpage.isExternalLink || false,
          submenu: []
        }));
        
        return {
          path: page.path || '',
          title: page.displayName || page.moduleName,
          icon: page.icon || 'ft-arrow-right submenu-icon',
          class: subpageRoutes.length > 0 ? 'has-sub' : '',
          badge: page.badge || '',
          badgeClass: page.badgeClass || '',
          isExternalLink: page.isExternalLink || false,
          submenu: subpageRoutes
        };
      });

      const moduleTitle = module.displayName || module.moduleName;
      const sortedPageRoutes = sortMasterDataSubmenu(moduleTitle, pageRoutes);
      
      // Check if module has subpages defined in navTree structure
      const hasSubpagesInNavTree = navTree.some(item => item.level === 2 && String(item.parentId) === String(module.id));

      let shouldShowModule = false;
      if (hasSubpagesInNavTree) {
        // Module has subpages in navTree (e.g. Master Data, Ingredients, System Settings):
        // It should be shown if it has accessible subpages OR module access is true.
        shouldShowModule = hasModuleAccess || sortedPageRoutes.length > 0;
      } else {
        // Standalone module without subpages in navTree (e.g. User, User Application, Dashboard, Billing, Recipe, Supplements):
        // It should only be shown if hasModuleAccess is true.
        shouldShowModule = hasModuleAccess;
      }

      if (shouldShowModule) {
        console.log(`  ✅ Adding module ${module.displayName} to menu (hasModuleAccess=${hasModuleAccess}, pageRoutes=${sortedPageRoutes.length})`);
        filteredRoutes.push({
          path: module.path || '',
          title: module.displayName || module.moduleName,
          icon: module.icon || 'assets/img/dashbord/icon/rework.png',
          class: sortedPageRoutes.length > 0 ? 'has-sub' : '',
          badge: module.badge || '',
          badgeClass: module.badgeClass || '',
          isExternalLink: module.isExternalLink || false,
          submenu: sortedPageRoutes
        });
      } else {
        console.log(`  ❌ Skipping module ${module.displayName} - no access`);
      }
    });
    
    console.log('Filtered routes:', filteredRoutes.length);

    // Post-process: collapse Supplements to a single role-aware link
    const supplementIdx = filteredRoutes.findIndex(r => {
      const t = ((r?.title || '') as string).trim().toLowerCase();
      return t === 'supplement' || t === 'supplements';
    });
    if (supplementIdx !== -1) {
      const supp = { ...filteredRoutes[supplementIdx] };
      supp.title = 'Supplements';
      supp.path = this.getSupplementLandingPath();
      supp.submenu = [];
      supp.class = (supp?.class || '').replace(/\bhas-sub\b/, '').trim();
      filteredRoutes[supplementIdx] = supp;
    }

    // Post-process: collapse Recipe to a single /recipe link (Add Recipe is a button inside the listing)
    const recipeIdx = filteredRoutes.findIndex(r => {
      const t = ((r?.title || '') as string).trim().toLowerCase();
      return t === 'recipe' || t === 'recipe management' || t === 'recipes';
    });
    if (recipeIdx !== -1) {
      const recipe = { ...filteredRoutes[recipeIdx] };
      recipe.path = '/recipe';
      recipe.submenu = [];
      recipe.class = (recipe?.class || '').replace(/\bhas-sub\b/, '').trim();
      filteredRoutes[recipeIdx] = recipe;
    }

    return filteredRoutes;
  }

  private getSupplementLandingPath(): string {
    const user = this.authService.getLoggedInUser();
    const currentRoleId = user?.currentRoleId || user?.roleIds?.[0];

    switch (currentRoleId) {
      case Role.Approver:
        return '/supplement/approver/supplements';
      case Role.Assigner:
        return '/supplement/assigner/supplements';
      case Role.Checker:
        return '/supplement/checker/supplements';
      case Role.Maker:
        return '/supplement/maker/recipelist';
      default:
        return '/supplement/superadmin/all';
    }
  }

  private postProcessMenuForRole(menu: RouteInfo[], userRole: number): RouteInfo[] {
    // Simply return the menu items that the user has permission for.
    // The path normalizations (Finish Product, Supplements) are already handled
    // by filterMenuByPermissions and vertical-menu.component.ts.
    return menu;
  }

  private cloneRoute(route: RouteInfo): RouteInfo {
    return {
      ...route,
      submenu: Array.isArray(route?.submenu)
        ? route.submenu.map(child => this.cloneRoute(child))
        : []
    };
  }

  /**
   * Defensive cleanup:
   * NavTree can occasionally contain duplicate rows (same module/page path + title),
   * which causes repeated sidebar items. Keep the first active row for each logical entry.
   */
  private deduplicateNavTree(navTree: any[]): any[] {
    const byKey = new Map<string, any>();

    for (const item of navTree) {
      if (!item) continue;
      const level = Number(item.level || 0);
      const parentId = Number(item.parentId || 0);
      const title = ((item.displayName || item.moduleName || '') as string).trim().toLowerCase();
      const path = ((item.path || '') as string).trim().toLowerCase();
      const key = `${level}|${parentId}|${title}|${path}`;

      // Prefer active records when duplicates exist.
      if (!byKey.has(key) || (!!item.isActive && !byKey.get(key)?.isActive)) {
        byKey.set(key, item);
      }
    }

    return Array.from(byKey.values());
  }

  /**
   * Check if user has module access
   */
  private hasModuleAccess(module: any, permissions: any[], userRole: number): boolean {
    const modulePermission = permissions.find(p => 
      String(p.navTreeId) === String(module.id) && 
      String(p.roleId) === String(userRole) && 
      p.permissionType === 'module' &&
      p.isAccess === true
    );
    return !!modulePermission;
  }

  /**
   * Check if user has page access
   */
  private hasPageAccess(page: any, permissions: any[], userRole: number): boolean {
    const pagePermission = permissions.find(p => 
      String(p.navTreeId) === String(page.id) && 
      String(p.roleId) === String(userRole) && 
      p.permissionType === 'page' &&
      p.isAccess === true
    );
    return !!pagePermission;
  }

  /**
   * Check if user has subpage access
   */
  private hasSubpageAccess(subpage: any, permissions: any[], userRole: number): boolean {
    const subpagePermission = permissions.find(p => 
      String(p.navTreeId) === String(subpage.id) && 
      String(p.roleId) === String(userRole) && 
      p.permissionType === 'subpage' &&
      p.isAccess === true
    );
    return !!subpagePermission;
  }

  /**
   * Build module route with filtered submenus
   */
  private buildModuleRoute(module: any, navTree: any[], permissions: any[], userRole: number): RouteInfo {
    const pages = navTree
      .filter(item => item.parentId === module.id && item.level === 2)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const filteredPages = pages
      .filter(page => this.hasPageAccess(page, permissions, userRole))
      .map(page => this.buildPageRoute(page, navTree, permissions, userRole))
      .filter(Boolean);

    return {
      path: module.path || '',
      title: module.displayName || module.moduleName,
      icon: module.icon || 'assets/img/dashbord/icon/rework.png',
      class: filteredPages.length > 0 ? 'has-sub' : '',
      badge: module.badge || '',
      badgeClass: module.badgeClass || '',
      isExternalLink: module.isExternalLink || false,
      submenu: filteredPages
    };
  }

  /**
   * Build page route with filtered submenus
   */
  private buildPageRoute(page: any, navTree: any[], permissions: any[], userRole: number): RouteInfo {
    const subpages = navTree
      .filter(item => item.parentId === page.id && item.level === 3)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const filteredSubpages = subpages
      .filter(subpage => this.hasSubpageAccess(subpage, permissions, userRole))
      .map(subpage => ({
        path: subpage.path || '',
        title: subpage.displayName || subpage.moduleName,
        icon: subpage.icon || 'ft-arrow-right submenu-icon',
        class: '',
        badge: subpage.badge || '',
        badgeClass: subpage.badgeClass || '',
        isExternalLink: subpage.isExternalLink || false,
        submenu: []
      }))
      .filter(Boolean);

    return {
      path: page.path || '',
      title: page.displayName || page.moduleName,
      icon: page.icon || 'ft-arrow-right submenu-icon',
      class: filteredSubpages.length > 0 ? 'has-sub' : '',
      badge: page.badge || '',
      badgeClass: page.badgeClass || '',
      isExternalLink: page.isExternalLink || false,
      submenu: filteredSubpages
    };
  }

  /**
   * Build subpage route
   */

  /**
   * Clear permission cache
   */
  clearCache(): void {
    this.permissionCache.clear();
    this.menuRefresh$.next();
  }

  /**
   * Clear cache for specific user
   */
  clearUserCache(userId: string): void {
    const keysToDelete = Array.from(this.permissionCache.keys()).filter(key => key.startsWith(`${userId}-`));
    keysToDelete.forEach(key => this.permissionCache.delete(key));
  }

  /**
   * Get role-specific pages for a module
   */
  getRoleSpecificPages(moduleName: string, userRole: number): string[] {
    const rolePageMap: { [key: string]: { [key: number]: string[] } } = {
      'ProductManagement': {
        1: ['AddFinishProduct', 'SuperAdmin', 'Checker', 'Maker'], // SuperAdmin
        4: ['AddFinishProduct', 'Checker'], // Checker
        5: ['AddFinishProduct', 'Maker'] // Maker
      },
      'RecipeManagement': {
        1: ['Recipes', 'AddRecipe'], // SuperAdmin
        2: ['Recipes', 'AddRecipe'], // Assigner
        3: ['Recipes', 'AddRecipe'], // Approver
        4: ['Recipes'], // Checker — only reviews, cannot create recipes
        5: ['Recipes'] // Maker — only works on assigned recipes, cannot create
      },
      'MasterData': {
        1: ['MealPreference', 'DietaryPreference', 'HealthColorCode', 'DataSource', 'DatabaseSource'] // SuperAdmin only
      }
    };

    return rolePageMap[moduleName]?.[userRole] || [];
  }

  /**
   * Get role-specific subpages for a page
   */
  getRoleSpecificSubpages(pageName: string, userRole: number): string[] {
    const roleSubpageMap: { [key: string]: { [key: number]: string[] } } = {
      'SuperAdmin': {
        1: ['SuperAdminDashboard', 'SuperAdminMaster', 'SuperAdminBilling'] // SuperAdmin only
      },
      'Checker': {
        1: ['CheckerDashboard', 'CheckerFinishProduct', 'CheckerBilling'], // SuperAdmin
        4: ['CheckerFinishProduct', 'CheckerBilling'] // Checker — dashboard lives under main Dashboard tab
      },
      'Maker': {
        1: ['MakerDashboard', 'MakerFinishProduct', 'MakerBilling'], // SuperAdmin
        5: ['MakerFinishProduct', 'MakerBilling'] // Maker — dashboard lives under main Dashboard tab
      }
    };

    return roleSubpageMap[pageName]?.[userRole] || [];
  }

  /**
   * Check if user has access to a specific path
   */
  async hasAccessToPath(path: string): Promise<boolean> {
    const user = this.authService.getLoggedInUser();
    if (!user) {
      return false;
    }

    const userRole = user.currentRoleId || user.roleIds?.[0] || 2;
    const permissions = await this.getUserPermissions(user.id, userRole);
    
    // Find the NavTree item for this path
    const navTree = await this.getNavTreeStructure();
    const navItem = navTree.find(item => item.path === path);
    
    if (!navItem) {
      return false;
    }

    // Check permission based on level
    const permissionType = navItem.level === 1 ? 'module' : 
                          navItem.level === 2 ? 'page' : 'subpage';
    
    const permission = permissions.find(p => 
      String(p.navTreeId) === String(navItem.id) && 
      String(p.roleId) === String(userRole) && 
      p.permissionType === permissionType &&
      p.isAccess === true
    );

    return !!permission;
  }

  /**
   * Get user's accessible modules
   */
  async getAccessibleModules(): Promise<string[]> {
    const user = this.authService.getLoggedInUser();
    if (!user) {
      return [];
    }

    const userRole = user.currentRoleId || user.roleIds?.[0] || 2;
    const permissions = await this.getUserPermissions(user.id, userRole);
    
    return permissions
      .filter(p => p.permissionType === 'module' && p.isAccess === true)
      .map(p => p.navTreeId);
  }

  /**
   * Get user's accessible pages for a module
   */
  async getAccessiblePages(moduleId: string): Promise<string[]> {
    const user = this.authService.getLoggedInUser();
    if (!user) {
      return [];
    }

    const userRole = user.currentRoleId || user.roleIds?.[0] || 2;
    const permissions = await this.getUserPermissions(user.id, userRole);
    
    return permissions
      .filter(p => p.permissionType === 'page' && p.isAccess === true)
      .map(p => p.navTreeId);
  }
}
